import { Skeleton, useToast, Flex } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useMemo } from 'react';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';

import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  getDefaultDashboardDisplayEndDate,
  getDefaultDashboardDisplayStartDate,
  errorToastConfig,
} from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';
import { useCreditFacilityContext } from '~/packages/credit-facility/hooks/CreditFacilityProvider';
import { useGetCurrentCreditAgreementFullArtifactQuery } from '~/packages/creditAgreements/requests/requests.gql.generated';
import {
  CreditAgreement,
  parseCreditAgreementFullArtifact,
} from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { useGetDailyConcentrationInsightsQuery } from '~/packages/portfolio/requests/requests.gql.generated';
import { generateConcentrationLimitChartConfig } from '~/packages/portfolio/ui/borrowingBase/portfolioBorrowingBaseChartConfigs';
import { PortfolioConcentrationLimitsChartTemplate } from '~/packages/portfolio/ui/borrowingBase/PortfolioConcentrationLimitsChartTemplate';
import { PortfolioExcessConcentrationChart } from '~/packages/portfolio/ui/borrowingBase/PortfolioExcessConcentrationChart';
import {
  ConcentrationLimitFormatMap,
  getConcentrationLimitFormatsByKey,
} from '~/packages/portfolio/ui/shared/portfolioChartUtils';

export const ConcentrationContainerV2 = () => {
  const toast = useToast();
  const { creditFacilityId } = useCreditFacilityContext();

  const {
    data: concentrationData,
    loading: isConcentrationDataLoading,
  } = useGetDailyConcentrationInsightsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityParams: {
        creditFacilityId,
        reportingDateStart: getDefaultDashboardDisplayStartDate(),
        reportingDateEnd: getDefaultDashboardDisplayEndDate(),
      },
    },
  });

  const {
    data: creditAgreementData,
    loading: isCreditAgreementLoading,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    variables: { creditFacilityId },
  });

  const concentrationLimitFormatMap = useMemo<ConcentrationLimitFormatMap>(() => {
    if (!concentrationData) {
      return new Map();
    }
    return getConcentrationLimitFormatsByKey(
      concentrationData.getDailyConcentrationInsights,
    );
  }, [concentrationData]);

  const creditAgreementConcentrationLimits: CreditAgreement.DomainEntity.ConcentrationLimitProperty[] = useMemo(() => {
    if (
      !creditAgreementData ||
      !creditAgreementData.currentCreditAgreementFullArtifact
    ) {
      return [];
    }

    const creditAgreementString =
      creditAgreementData.currentCreditAgreementFullArtifact.creditAgreement;
    const creditAgreementId =
      creditAgreementData.currentCreditAgreementFullArtifact.id;

    let parsedCreditAgreement = null;
    if (creditAgreementString) {
      try {
        parsedCreditAgreement = parseCreditAgreementFullArtifact(
          creditAgreementString,
        );
      } catch (err) {
        Sentry.captureException(err);

        toast({
          ...errorToastConfig,
          id: 'errorGetCurrentCreditAgreementFullArtifact',
          title: `Error parsing credit agreement artifact for credit agreement ${creditAgreementId}`,
        });
      }
    }

    finleyAssert(
      parsedCreditAgreement,
      `Expect currentCreditAgreementFullArtifact to exist for creditFacilityId ${creditFacilityId}`,
    );

    const singleLoanProductConcentrationLimits = parsedCreditAgreement.borrowingBaseMechanics.calcAgentMechanics.loanProductMechanics.flatMap(
      (loanProductMechanic) => loanProductMechanic.concentrationLimits,
    );
    const multiLoanProductConcentrationLimits =
      parsedCreditAgreement.borrowingBaseMechanics.calcAgentMechanics
        .multiLoanProductConcentrationLimits;

    return [
      ...singleLoanProductConcentrationLimits,
      ...multiLoanProductConcentrationLimits,
    ];
  }, [creditAgreementData, creditFacilityId, toast]);

  if (
    !concentrationData ||
    isConcentrationDataLoading ||
    !creditAgreementData ||
    isCreditAgreementLoading
  ) {
    return <Skeleton data-testid="loading-insights-view" height="600px" />;
  }

  return (
    <Flex direction="column" gap="2.5rem">
      <PortfolioExcessConcentrationChart
        creditFacilityId={creditFacilityId}
        chartTitle="Excess Concentration Amounts"
        concentrationLimitFormatMap={concentrationLimitFormatMap}
        creditAgreementConcentrationLimits={creditAgreementConcentrationLimits}
      />
      {concentrationLimitFormatMap.size > 0 && (
        <Flex direction="column" gap="1rem">
          <Typography variant="section-title" trim>
            Concentration Limits
          </Typography>

          {Array.from(concentrationLimitFormatMap).map(
            ([concentrationLimitKey, valueType]) => {
              const creditAgreementConcentrationLimit = creditAgreementConcentrationLimits.find(
                (concentrationLimit) =>
                  concentrationLimit._config_.concentrationLimitConfig?.propertyKey.toLowerCase() ===
                  concentrationLimitKey.toLowerCase(),
              );

              const chartTitle =
                creditAgreementConcentrationLimit?.name ??
                commonUtils.toTitleCase(concentrationLimitKey);

              return (
                <PortfolioConcentrationLimitsChartTemplate
                  creditFacilityId={creditFacilityId}
                  chartTitle={chartTitle}
                  chartConfig={generateConcentrationLimitChartConfig(
                    valueType === 'number' ? 'float' : valueType,
                  )}
                  concentrationLimitKey={concentrationLimitKey}
                  key={concentrationLimitKey}
                />
              );
            },
          )}
        </Flex>
      )}
    </Flex>
  );
};
